import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Collapse,
    Switch,
    FormControlLabel,
    useTheme,
    alpha
} from '@mui/material';
import {
    ExpandLess,
    ExpandMore,
    Animation as AnimationIcon
} from '@mui/icons-material';

interface FunctionWrapperProps {
    title: string;
    isExpanded: boolean;
    onToggleExpanded: (expanded: boolean) => void;
    animationType?: 'fall' | 'shake';
    enabled?: boolean;
    onToggleEnabled?: (enabled: boolean) => void;
    children: React.ReactNode;
}

const FunctionWrapper: React.FC<FunctionWrapperProps> = ({
    title,
    isExpanded,
    onToggleExpanded,
    animationType,
    enabled,
    onToggleEnabled,
    children
}) => {
    const theme = useTheme();

    return (
        <Paper 
            sx={{ 
                mb: 2, 
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                    boxShadow: theme.shadows[4]
                }
            }}
        >
            {/* 标题栏 */}
            <Box 
                sx={{ 
                    p: 2, 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    }
                }}
            >
                {/* 左侧：标题和开关 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontWeight: 600,
                            color: theme.palette.primary.main
                        }}
                    >
                        {title}
                    </Typography>
                    
                    {/* 启用/禁用开关 */}
                    {onToggleEnabled !== undefined && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={enabled || false}
                                    onChange={(e) => onToggleEnabled(e.target.checked)}
                                    size="small"
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2" color="text.secondary">
                                    {enabled ? '已启用' : '未启用'}
                                </Typography>
                            }
                            onClick={(e) => e.stopPropagation()}
                            sx={{ margin: 0 }}
                        />
                    )}
                </Box>

                {/* 右侧：展开/收起按钮 */}
                <IconButton 
                    size="small"
                    onClick={() => onToggleExpanded(!isExpanded)}
                    sx={{ 
                        color: theme.palette.primary.main,
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease-in-out'
                    }}
                >
                    <ExpandMore />
                </IconButton>
            </Box>
            
            {/* 内容区 */}
            <Box>
                {/* 动画区域 - 折叠时显示 */}
                <Collapse in={!isExpanded}>
                    <Box sx={{ 
                        height: 300,
                        width: '100%',
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.background.paper, 0.8),
                        borderTop: `1px solid ${theme.palette.divider}`,
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <AnimationPlaceholder type={animationType} />
                    </Box>
                </Collapse>
                
                {/* 设置区域 - 展开时显示 */}
                <Collapse in={isExpanded}>
                    <Box sx={{ 
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper
                    }}>
                        {children}
                    </Box>
                </Collapse>
            </Box>
        </Paper>
    );
};

// 动画占位组件
const AnimationPlaceholder: React.FC<{ type?: 'fall' | 'shake' }> = ({ type }) => {
    const theme = useTheme();
    
    const getAnimationContent = () => {
        switch (type) {
            case 'fall':
                return {
                    icon: '🚶‍♂️',
                    title: '摔倒检测',
                    description: '检测到意外跌倒时自动触发紧急呼救',
                    color: theme.palette.error.main
                };
            case 'shake':
                return {
                    icon: '📱',
                    title: '摇一摇报警',
                    description: '快速摇动手机触发紧急呼救',
                    color: theme.palette.warning.main
                };
            default:
                return {
                    icon: '🚨',
                    title: '紧急呼救',
                    description: '紧急情况下的自动求助功能',
                    color: theme.palette.primary.main
                };
        }
    };

    const content = getAnimationContent();

    return (
        <Box sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            px: 4
        }}>
            <Box sx={{ 
                fontSize: 72, 
                mb: 2,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.1)' },
                    '100%': { transform: 'scale(1)' }
                }
            }}>
                {content.icon}
            </Box>
            <Typography 
                variant="h5" 
                sx={{ 
                    mb: 1, 
                    fontWeight: 600,
                    color: content.color
                }}
            >
                {content.title}
            </Typography>
            <Typography 
                variant="body1" 
                sx={{ 
                    color: 'text.secondary',
                    lineHeight: 1.6
                }}
            >
                {content.description}
            </Typography>
            <Box sx={{
                mt: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                color: 'text.disabled'
            }}>
                <AnimationIcon sx={{ fontSize: 20 }} />
                <Typography variant="caption">
                    点击展开设置
                </Typography>
            </Box>
        </Box>
    );
};

export default FunctionWrapper;